import React, { useState, useRef } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import Textarea from "@amzn/meridian/textarea";
import Checkbox from "@amzn/meridian/checkbox";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-large";
import { InspectionResult, ComplianceFixWhen } from "src/services/inspectionService";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { fixInspectionResult, rememberReason } from "src/store/inspectionResultsSlice";
import useKeyboardScrollReset from "src/hooks/useKeyboardScrollReset";
import ManualCaptureMultipleStep from "../steps/ManualCaptureMultipleStep";
import ReasonTag from "./ReasonTag";
import { pickReuseReason } from "./reasonTags";
import { EvidencePhoto } from "./evidence";
import "./fixItNowPage.scss";

/**
 * "Fix it now" page for a non-compliant result: the inspector attests that the
 * flagged physical condition was corrected on site, providing new evidence photos
 * and a comment. Distinct from a manual override (which disputes the AI verdict) —
 * this records a real remediation, so the backend closes the finding.
 */
const FixItNowPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [comment, setComment] = useState<string>("");
    const [commentTouched, setCommentTouched] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [evidence, setEvidence] = useState<EvidencePhoto[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [useOriginalPhoto, setUseOriginalPhoto] = useState(false);

    useKeyboardScrollReset();

    const suggestedResolutionReason = useAppSelector((rootState) => rootState.inspectionResults.suggestedResolutionReason);
    const reuseReason = pickReuseReason(suggestedResolutionReason);

    const state = location.state as {
        result: InspectionResult;
        questionText: string;
        objectCheck?: { object_name: string };
    } | null;

    if (!state) {
        return <Navigate to={`/configurationDrivenInspections/${inspectionId}/review`} replace />;
    }

    const { result, questionText, objectCheck } = state;

    const getGuidanceMessage = (): string => {
        if (objectCheck) {
            const displayName = objectCheck.object_name.replace(/\s*sign\s*$/i, "").trim();
            return `Take photos showing ${displayName} in its corrected state`;
        }
        return "Take photos showing the corrected condition";
    };

    const addEvidenceFiles = (files: File[]) => {
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                setEvidence((prev) => [...prev, { file, preview: event.target?.result as string }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleRemoveEvidence = (index: number) => {
        setEvidence((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCameraSubmit = (files: File[]) => {
        addEvidenceFiles(files);
        setShowCamera(false);
    };

    // Fill the comment field from the reuse tag.
    const handleReuseTagTap = (tagText: string) => {
        setComment(tagText);
        setCommentTouched(true);
        setSubmitError(null);
    };

    const handleAttach = () => {
        if (fileInputRef.current) {
            fileInputRef.current.removeAttribute("capture");
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        addEvidenceFiles(files);

        e.target.value = "";
    };

    const trimmedComment = comment.trim();
    const hasComment = trimmedComment !== "";

    const canSubmit =
        hasComment && confirmed && (useOriginalPhoto || evidence.length > 0);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setSubmitError(null);

        const formData = new FormData();
        formData.append("when", ComplianceFixWhen.NOW);
        formData.append("result_id", result.resultId);
        formData.append("comment", trimmedComment);
        if (objectCheck) {
            formData.append("object_name", objectCheck.object_name);
        }

        formData.append("use_original_photo", String(useOriginalPhoto));

        if (!useOriginalPhoto) {
            evidence.forEach(({ file }) => {
                formData.append("files", file);
            });
        }

        try {
            await dispatch(fixInspectionResult({
                inspection_id: inspectionId!,
                question_id: result.questionId,
                formData,
            })).unwrap();
            dispatch(rememberReason(trimmedComment));

            navigate(-1);
        } catch (error) {
            console.error("Failed to submit fix:", error);
            const message =
                typeof error === "string"
                    ? error
                    : error instanceof Error
                        ? error.message
                        : "Failed to mark as fixed. Please try again.";
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (showCamera) {
        return (
            <ManualCaptureMultipleStep
                humanText={getGuidanceMessage()}
                activeQuestionIds={[]}
                onComplete={() => setShowCamera(false)}
                onSubmitFiles={handleCameraSubmit}
            />
        );
    }

    return (
        <div className="fix-it-now-page pwa-scrollable">
            <Column spacingInset="500" spacing="500">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="fix-it-now-page__back"
                        onClick={() => navigate(-1)}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="success" />
                    </div>
                    <Text type="b300" color="primary">
                        Fix it now
                    </Text>
                </Row>

                <div className="fix-it-now-page__info-banner">
                    <Text type="b200" color="primary">
                        Use this only when the condition has actually been corrected on site. To
                        dispute the AI result instead, go back and choose Manual Override.
                    </Text>
                </div>

                <p className="fix-it-now-page__question-text">{questionText}</p>

                <Column spacing="100">
                    <Text type="h100" color="success">Add evidence</Text>
                    <Text type="b100" color="secondary">
                        Photos are timestamped.
                    </Text>
                </Column>

                <Checkbox
                    checked={useOriginalPhoto}
                    onChange={setUseOriginalPhoto}
                    disabled={evidence.length > 0}
                >
                    Use original photo as evidence (No new photo needed)
                </Checkbox>

                <Row spacing="300" alignmentHorizontal="center">
                    <div
                        className={`fix-it-now-page__evidence-box ${useOriginalPhoto ? "fix-it-now-page__evidence-box--disabled" : ""}`}
                        onClick={() => {
                            if (useOriginalPhoto) return;
                            setShowCamera(true);
                        }}
                        role="button"
                    >
                        <Icon tokens={cameraTokens} color="success" />
                        <Text type="b100" color="secondary">Capture</Text>
                    </div>
                    <Text type="b100" color="secondary">OR</Text>
                    <div
                        className={`fix-it-now-page__evidence-box ${useOriginalPhoto ? "fix-it-now-page__evidence-box--disabled" : ""}`}
                        onClick={() => {
                            if (useOriginalPhoto) return;
                            handleAttach();
                        }}
                        role="button"
                    >
                        <Icon tokens={uploadTokens} color="success" />
                        <Text type="b100" color="secondary">Attach</Text>
                    </div>
                </Row>

                {evidence.length > 0 && (
                    <Row spacing="300" wrap="down">
                        {evidence.map(({ preview }, idx) => (
                            <div key={idx} className="fix-it-now-page__thumbnail-wrapper">
                                <img
                                    src={preview}
                                    alt={`Evidence ${idx + 1}`}
                                    className="fix-it-now-page__thumbnail"
                                />
                                <button
                                    type="button"
                                    className="fix-it-now-page__thumbnail-remove"
                                    aria-label={`Remove evidence ${idx + 1}`}
                                    onClick={() => handleRemoveEvidence(idx)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </Row>
                )}

                <Column spacing="100">
                    <Text type="h100" color="success">Add comments *</Text>
                    <Textarea
                        value={comment}
                        onChange={(value: string) => {
                            setComment(value);
                            setCommentTouched(true);
                            setSubmitError(null);
                        }}
                        onBlur={() => setCommentTouched(true)}
                        placeholder="Describe what was corrected..."
                        rows={3}
                        error={commentTouched && !hasComment}
                        errorMessage={
                            commentTouched && !hasComment
                                ? "A comment is required to mark a result as fixed."
                                : undefined
                        }
                    />
                </Column>

                {reuseReason && (
                    <Row spacing="300" wrap="down">
                        <ReasonTag
                            text={reuseReason}
                            variant="dashed"
                            selected={false}
                            onTap={handleReuseTagTap}
                        />
                    </Row>
                )}

                <Checkbox checked={confirmed} onChange={setConfirmed}>
                    I confirm the condition has been physically corrected and the evidence above
                    shows the corrected state.
                </Checkbox>

                {submitError && (
                    <Text type="b100" color="error">{submitError}</Text>
                )}
            </Column>

            <button
                className={`fix-it-now-page__submit-btn ${canSubmit ? "fix-it-now-page__submit-btn--enabled" : ""}`}
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
            >
                {isSubmitting ? "SUBMITTING..." : "MARK AS FIXED"}
            </button>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
            />
        </div>
    );
};

export default FixItNowPage;
